/**
 * Phase N.3 — modal for editing an invoice's issue_date + terms while
 * it's still in draft. Live-updates the due-date preview client-side
 * from the same net_7 / net_15 / net_30 / due_on_receipt → days
 * mapping the backend uses (see ``_due_date_iso`` in
 * ``routers/invoices.py``).
 *
 * Fires ``PATCH /admin/invoices/{id}`` with only the fields that
 * actually changed. On success, calls ``onSaved(newInvoiceDoc)`` so
 * the parent can hot-swap the invoice in place without a full refetch.
 *
 * If the invoice raced from ``draft → sent`` between open and save the
 * backend returns 400 with ``{code: "invalid_state_for_edit",
 * current_status: "sent"}`` — we surface a targeted error message and
 * keep the modal open so the operator sees why.
 */
import React, { useMemo, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const TERMS_OPTIONS = [
    { value: "due_on_receipt", label: "Due on receipt", days: 0 },
    { value: "net_7", label: "Net 7", days: 7 },
    { value: "net_15", label: "Net 15", days: 15 },
    { value: "net_30", label: "Net 30", days: 30 },
];

const TERMS_DAYS = Object.fromEntries(
    TERMS_OPTIONS.map((t) => [t.value, t.days]),
);

function addDaysIso(iso, days) {
    // Parse as YYYY-MM-DD and add days without timezone drift.
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + days);
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(dt.getUTCDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
}

export default function InvoiceDetailsEditModal({
    open,
    invoice,
    onOpenChange,
    onSaved,
}) {
    const [issueDate, setIssueDate] = useState(invoice?.issue_date || "");
    const [terms, setTerms] = useState(invoice?.terms || "net_30");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    // Reset local state each time the modal opens with a fresh invoice.
    React.useEffect(() => {
        if (open) {
            setIssueDate(invoice?.issue_date || "");
            setTerms(invoice?.terms || "net_30");
            setErr("");
        }
    }, [open, invoice?.id, invoice?.issue_date, invoice?.terms]);

    const previewDueDate = useMemo(() => {
        const days = TERMS_DAYS[terms];
        if (!issueDate || days == null) return "";
        return addDaysIso(issueDate, days);
    }, [issueDate, terms]);

    const dirty =
        (invoice?.issue_date || "") !== issueDate
        || (invoice?.terms || "") !== terms;

    const save = async () => {
        if (!dirty) {
            onOpenChange?.(false);
            return;
        }
        setBusy(true);
        setErr("");
        try {
            const body = {};
            if ((invoice?.issue_date || "") !== issueDate) body.issue_date = issueDate;
            if ((invoice?.terms || "") !== terms) body.terms = terms;
            const { data } = await api.patch(`/admin/invoices/${invoice.id}`, body);
            toast.success("Invoice details updated");
            onSaved?.(data);
            onOpenChange?.(false);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "invalid_state_for_edit") {
                setErr(
                    `This invoice can no longer be edited (status is now `
                    + `${detail.current_status || "not draft"}).`,
                );
            } else {
                setErr(formatApiError(e) || "Failed to update invoice.");
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="invoice-details-edit-modal">
                <DialogHeader>
                    <DialogTitle>Edit invoice details</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-wider text-slate-500">
                            Issue date
                        </label>
                        <Input
                            type="date"
                            value={issueDate}
                            onChange={(e) => setIssueDate(e.target.value)}
                            data-testid="invoice-edit-issue-date"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs uppercase tracking-wider text-slate-500">
                            Terms
                        </label>
                        <Select value={terms} onValueChange={setTerms}>
                            <SelectTrigger data-testid="invoice-edit-terms-trigger">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {TERMS_OPTIONS.map((t) => (
                                    <SelectItem
                                        key={t.value}
                                        value={t.value}
                                        data-testid={`invoice-edit-terms-option-${t.value}`}
                                    >
                                        {t.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <p
                        className="text-sm text-slate-700 rounded-md bg-slate-50 border border-slate-200 px-3 py-2"
                        data-testid="invoice-edit-due-date-preview"
                    >
                        New due date will be:{" "}
                        <span className="font-semibold tabular-nums">
                            {previewDueDate || "—"}
                        </span>
                    </p>
                    {err && (
                        <p
                            className="text-sm text-red-700 rounded-md bg-red-50 border border-red-200 px-3 py-2"
                            data-testid="invoice-edit-error"
                        >
                            {err}
                        </p>
                    )}
                </div>
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange?.(false)}
                        disabled={busy}
                        data-testid="invoice-edit-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={save}
                        disabled={busy || !dirty || !issueDate}
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        data-testid="invoice-edit-save-btn"
                    >
                        <Save size={14} /> Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
